import React from "react";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import TrendingUp from "@mui/icons-material/TrendingUp";
import TrendingDown from "@mui/icons-material/TrendingDown";
import ReceiptLong from "@mui/icons-material/ReceiptLong";
import AccountBalanceWallet from "@mui/icons-material/AccountBalanceWallet";
import { useTranslation } from "react-i18next";

const formatAmount = (value) =>
  `${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ؋`;

const KpiCard = ({ title, value, subtitle, icon: Icon, color }) => {
  const light = alpha(color, 0.12);
  const border = alpha(color, 0.22);

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: `1px solid ${border}`,
        background: `linear-gradient(160deg, ${light} 0%, #fff 58%)`,
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: color,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: light,
              color: color,
            }}
          >
            <Icon />
          </Box>
        </Box>
        <Typography
          variant="h4"
          sx={{
            mt: 1.5,
            fontWeight: 800,
            color: color,
            fontSize: { xs: "1.7rem", md: "2rem" },
            lineHeight: 1.2,
            wordBreak: "break-word",
          }}
        >
          {formatAmount(value)}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

const SummaryKpiCards = ({ summary = {} }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const totalRevenue = Number(summary.total_income) || 0;
  const totalExpenses = Number(summary.total_expenses) || 0;
  const profit =
    summary.net_income != null
      ? Number(summary.net_income)
      : totalRevenue - totalExpenses;
  const isLoss = profit < 0;

  return (
    <Grid container spacing={2.5} sx={{ mb: 1 }}>
      <Grid item xs={12} md={4}>
        <KpiCard
          title={t("finance.totalRevenue")}
          value={totalRevenue}
          subtitle={t("finance.revenueFromSales")}
          icon={TrendingUp}
          color={theme.palette.success.main}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <KpiCard
          title={t("finance.totalExpenses")}
          value={totalExpenses}
          subtitle={t("finance.expenseFromOperations")}
          icon={ReceiptLong}
          color={theme.palette.error.main}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <KpiCard
          title={t("finance.profit")}
          value={profit}
          subtitle={t("finance.profitHint")}
          icon={isLoss ? TrendingDown : AccountBalanceWallet}
          color={isLoss ? theme.palette.error.main : theme.palette.primary.main}
        />
      </Grid>
    </Grid>
  );
};

export default SummaryKpiCards;
